import React, {useEffect} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../../../redux/store";

// Import slice e types - PATH RELATIVI
import {
    getOrdineLavoro,
    deleteOrdineLavoro,
    setOrdineLavoroCorrenteId,
} from "../ordiniLavoroSlice";

import {
    OrdineLavoro,
    OrdiniLavoroList,
    STATI_ORDINE_LAVORO,
    getStatoOrdineLavoroColor,
    isOrdineLavoroInRitardo,
} from "../ordiniLavoroTypes";

import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {Routes_path} from "components/routerApp";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import {useTheme} from "@mui/material/styles";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Components - PATH RELATIVI
import OrdineLavoroPageBreadcrumbs from "./OrdineLavoroPageBreadcrumbs";
import OrdineLavoroForm from "./OrdineLavoroForm";
import AssegnazioniCard from "./assegnazioni/AssegnazioniCard";
import AllegatiPreview from "../../../../allegati/AllegatiPreview";

// ============================================
// COMPONENT
// ============================================

const OrdineLavoroPage = () => {
    const {ordine_lavoro_id} = useParams<{ ordine_lavoro_id: string }>();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    const ordineLavoroId = ordine_lavoro_id ? parseInt(ordine_lavoro_id, 10) : undefined;

    // Se non c'è ID, redirect alla lista (non si può creare da qui)
    useEffect(() => {
        if (!ordineLavoroId) {
            navigate(Routes_path.ORDINI_LAVORO);
        }
    }, [ordineLavoroId, navigate]);

    // ============================================
    // SELECTORS
    // ============================================

    const ordineLavoroCorrente = useSelector<RootState, OrdineLavoro | undefined>(
        (state) =>
            state.ordiniLavoroState.ordiniLavoroList.results.find(
                (item) => item.id === state.ordiniLavoroState.ordineLavoroCorrenteId
            )
    );

    const ordiniLavoroList = useSelector<RootState, OrdiniLavoroList>(
        (state) => state.ordiniLavoroState.ordiniLavoroList
    );

    const ordineLavoroStatus = ordiniLavoroList.errorsStack.status;

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        if (ordineLavoroId) {
            dispatch(setOrdineLavoroCorrenteId(ordineLavoroId));
            dispatch(getOrdineLavoro({ordineLavoroId}));
        }

        return () => {
            dispatch(setOrdineLavoroCorrenteId(undefined));
        };
    }, [dispatch, ordineLavoroId]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleBack = () => {
        navigate(Routes_path.ORDINI_LAVORO);
    };

    const handleDelete = async () => {
        if (!ordineLavoroCorrente) return;

        const confirmMessage = `Sei sicuro di voler eliminare l'ordine di lavoro #${ordineLavoroCorrente.id}?`;

        if (window.confirm(confirmMessage)) {
            await dispatch(deleteOrdineLavoro({ordineLavoroToDelete: ordineLavoroCorrente}));
            navigate(Routes_path.ORDINI_LAVORO);
        }
    };

    // ============================================
    // RENDER
    // ============================================

    if (!ordineLavoroId) {
        return null;
    }

    return (
        <Box sx={{p: 2, maxWidth: 1800, margin: "0 auto"}}>
            {/* Breadcrumbs */}
            <Box sx={{mb: 2}}>
                <OrdineLavoroPageBreadcrumbs
                    ordineLavoroId={ordineLavoroId}
                    codiceItem={ordineLavoroCorrente?.codice_item}
                />
            </Box>

            {/* Header con azioni */}
            <Card sx={{mb: 2, backgroundColor: theme.palette.grey[50]}}>
                <CardHeader
                    title={
                        <Grid container sx={{alignItems: "center"}} spacing={2}>
                            <Grid size={{xs: 12, sm: 6}}>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{alignItems: "center"}}
                                >
                                    <IconButton onClick={handleBack} size="small">
                                        <ArrowBackIcon/>
                                    </IconButton>
                                    <Typography variant="h5">
                                        Ordine di Lavoro #{ordineLavoroId}
                                    </Typography>
                                    {ordineLavoroCorrente?.stato && (
                                        <Chip
                                            label={
                                                STATI_ORDINE_LAVORO[
                                                    ordineLavoroCorrente.stato as keyof typeof STATI_ORDINE_LAVORO
                                                    ] || ordineLavoroCorrente.stato
                                            }
                                            color={getStatoOrdineLavoroColor(ordineLavoroCorrente.stato)}
                                            size="small"
                                        />
                                    )}
                                    {ordineLavoroCorrente && isOrdineLavoroInRitardo(ordineLavoroCorrente) && (
                                        <Chip
                                            icon={<WarningAmberIcon/>}
                                            label={`${ordineLavoroCorrente.giorni_ritardo}g ritardo`}
                                            color="error"
                                            size="small"
                                        />
                                    )}
                                    {!!ordineLavoroCorrente?.quantita_difforme && (
                                        <Chip
                                            icon={<WarningAmberIcon/>}
                                            label={`Q.tà difforme: ${ordineLavoroCorrente?.quantita_difforme}`}
                                            color="warning"
                                            size="small"
                                        />
                                    )}
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{justifyContent: "flex-end"}}
                                >
                                    <Button
                                        startIcon={<DeleteIcon/>}
                                        onClick={handleDelete}
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                    >
                                        Elimina
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    }
                />
            </Card>

            {/* Alert per errori generali */}
            {ordineLavoroCorrente?.errorsStack?.fieldsErrors?.non_field_errors && (
                <Alert severity="error" sx={{mb: 2}}>
                    {ordineLavoroCorrente.errorsStack.fieldsErrors.non_field_errors.toString()}
                </Alert>
            )}

            {/* Info Previsione (read-only) */}
            {ordineLavoroCorrente && (
                <Card sx={{mb: 2}}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Informazioni Previsione
                        </Typography>
                        <Divider sx={{mb: 2}}/>
                        <Grid container spacing={2}>
                            <Grid size={{xs: 12, sm: 10, md: 9}}>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 12, sm: 6, md: 4}}>
                                        <Typography variant="caption" color="text.secondary">
                                            Codice Articolo
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {ordineLavoroCorrente.codice_item || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 6, md: 4}}>
                                        <Typography variant="caption" color="text.secondary">
                                            Descrizione
                                        </Typography>
                                        <Typography variant="body1">
                                            {ordineLavoroCorrente.descrizione_item || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 6, md: 4}}>
                                        <Typography variant="caption" color="text.secondary">
                                            Stagione
                                        </Typography>
                                        <Typography variant="body1">
                                            {ordineLavoroCorrente.stagione_display || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 6, md: 4}}>
                                        <Typography variant="caption" color="text.secondary">
                                            Mandatario
                                        </Typography>
                                        <Typography variant="body1">
                                            {ordineLavoroCorrente.mandatario_display || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 6, md: 4}}>
                                        <Typography variant="caption" color="text.secondary">
                                            Confezionista
                                        </Typography>
                                        <Typography variant="body1">
                                            {ordineLavoroCorrente.confezionista_display || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 6, md: 4}}>
                                        <Typography variant="caption" color="text.secondary">
                                            Tecnica
                                        </Typography>
                                        <Typography variant="body1">
                                            {ordineLavoroCorrente.tecnica_display || "-"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={{xs: 12, sm: 2, md: 3}}>
                                <AllegatiPreview previsione_id={ordineLavoroCorrente.previsione}/>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Form principale */}
            <Card>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Dati Ordine di Lavoro
                    </Typography>
                    <Divider sx={{mb: 2}}/>

                    {ordineLavoroCorrente ? (
                        <OrdineLavoroForm ordineLavoro={ordineLavoroCorrente}/>
                    ) : (
                        ordineLavoroStatus !== ErrorStatusTypes.PENDING && (
                            <Alert severity="warning">
                                Ordine di lavoro non trovato
                            </Alert>
                        )
                    )}
                    {ordineLavoroCorrente && ordineLavoroId && (
                        <AssegnazioniCard
                            ordineLavoro={ordineLavoroCorrente}
                        />
                    )}

                </CardContent>
            </Card>

            {/* Loading overlay */}
            <Backdrop
                open={ordineLavoroStatus === ErrorStatusTypes.PENDING}
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
    );
};

export default OrdineLavoroPage;